import { audio, soundEnabledSignal } from '@/audio-singleton';
import { useState } from 'preact/hooks';
import { BottomSheet } from './BottomSheet';
import { createUiPrefs, type UiPrefs } from '@/lib/ui-prefs';
import { t, setLocale } from '@/i18n';
import { gameSignals } from '@/bridge/gameSignals';
import { SOUND_UI_ENABLED } from '@/lib/features';

export interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  onRequestReset: () => void;
  /** Optional: App passes the lifted shared singleton; omitted callers fall back
   *  to a per-instance createUiPrefs(window.localStorage) (unchanged behavior). */
  prefs?: UiPrefs;
}

function localeUnlocked(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.get('locale') === 'sk') return true;
  } catch { /* ignore */ }
  return false;
}

export function SettingsModal({ open, onClose, onRequestReset, prefs }: SettingsModalProps) {
  const prefsRef = prefs ?? createUiPrefs(window.localStorage);
  audio(); // ensure audio singleton + soundEnabledSignal are initialized
  const sound = soundEnabledSignal.value;
  const [override, setOverride] = useState(prefsRef.getReducedMotionOverride());
  const [hardMode, setHardMode] = useState(prefsRef.getHardModeEnabled());
  const [assist, setAssist] = useState(prefsRef.getAssistEnabled());
  const unlocked = localeUnlocked();
  const current = gameSignals.currentLocale.value;
  const version = '0.13.0';

  return (
    <BottomSheet open={open} onClose={onClose} ariaLabel={t('settings.title')}>
      <div class="px-5 pb-5 space-y-4">
        <h2 class="text-lg font-bold">{t('settings.title')}</h2>

        {SOUND_UI_ENABLED && (
          <label class="flex items-center justify-between">
            <span>{t('settings.sound')}</span>
            <input
              type="checkbox"
              checked={sound}
              aria-label={t('settings.sound')}
              onChange={() => audio().setEnabled(!sound)}
            />
          </label>
        )}

        <label class="flex items-center justify-between">
          <span>
            {t('settings.hardMode')}
            <span class="block text-xs text-gray-500">{t('settings.hardModeHelp')}</span>
          </span>
          <input
            type="checkbox"
            checked={hardMode}
            aria-label={t('settings.hardMode')}
            onChange={() => { const next = !hardMode; setHardMode(next); prefsRef.setHardModeEnabled(next); }}
          />
        </label>

        <label class="flex items-center justify-between">
          <span>
            {t('settings.assist')}
            <span class="block text-xs text-gray-500">{t('settings.assistHelp')}</span>
          </span>
          <input
            type="checkbox"
            checked={assist}
            aria-label={t('settings.assist')}
            onChange={() => { const next = !assist; setAssist(next); prefsRef.setAssistEnabled(next); }}
          />
        </label>

        <label class="flex items-center justify-between">
          <span>{t('settings.reducedMotion')}</span>
          <select
            value={override === null ? 'auto' : override ? 'on' : 'off'}
            aria-label={t('settings.reducedMotion')}
            onChange={(e) => {
              const v = (e.currentTarget as HTMLSelectElement).value;
              const next = v === 'auto' ? null : v === 'on';
              setOverride(next);
              prefsRef.setReducedMotionOverride(next);
            }}
          >
            <option value="auto">auto</option>
            <option value="on">on</option>
            <option value="off">off</option>
          </select>
        </label>

        <div class="flex items-center justify-between">
          <span>{t('settings.locale')}</span>
          {unlocked ? (
            <div class="flex gap-2">
              <button
                class={`px-2 py-1 rounded ${current === 'hu' ? 'bg-[#1a3a5c] text-white' : 'bg-gray-200'}`}
                onClick={() => { audio().play('tap'); setLocale('hu'); }}
              >HU</button>
              <button
                class={`px-2 py-1 rounded ${current === 'sk' ? 'bg-[#1a3a5c] text-white' : 'bg-gray-200'}`}
                onClick={() => { audio().play('tap'); setLocale('sk'); }}
              >SK</button>
            </div>
          ) : (
            <span aria-disabled="true" class="text-sm text-gray-500">{t('settings.localeComingSoon')}</span>
          )}
        </div>

        <button
          class="w-full bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2"
          onClick={() => { audio().play('tap'); onRequestReset(); }}
        >{t('settings.resetProgress')}</button>

        <p class="text-xs text-gray-500 text-center pt-2">{t('settings.versionFooter', { version })}</p>
      </div>
    </BottomSheet>
  );
}
